import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase/app";
import {
  aiUsageLimitFirestoreData,
  decodeAIUsageSummary,
  parseFreeTierAIUsageLimit,
  type AIUsageLimit,
  type AIUsagePeriod,
  type AIUsageSummary,
} from "../models/aiUsageLimits";
import {
  leagueAIUsageClientWritePayload,
  leagueBillingFirestoreData,
  type LeagueBilling,
} from "../models/leagueBilling";
import type { SBotUser } from "../models/user";
import { humanActionAuditActor, recordActionAudit, type ActionAuditRecorder } from "./actionAudit";
import { apiRequest } from "./functionsClient";

export const AI_CONFIG_COLLECTION = "sbotAIConfig";
export const AI_USAGE_LIMITS_DOCUMENT_ID = "aiUsageLimits";

export class AIUsageLimitInputError extends Error {
  readonly code: "notAuthorized" | "invalidTotalTokens" | "notPaidTier";

  constructor(code: AIUsageLimitInputError["code"], message: string) {
    super(message);
    this.name = "AIUsageLimitInputError";
    this.code = code;
  }
}

export interface AIUsageSummaryRequest {
  leagueID?: string;
  includeBreakdown?: boolean;
  period?: AIUsagePeriod;
}

export interface AIUsageLimitsServiceDependencies {
  readFreeTierDocument(): Promise<Record<string, unknown> | undefined>;
  writeFreeTierDocument(payload: Record<string, unknown>): Promise<void>;
  writePaidLeagueLimit(leagueID: string, payload: Record<string, unknown>): Promise<void>;
  requestSummary(body: Record<string, unknown>): Promise<unknown>;
  recordAudit: ActionAuditRecorder;
  currentEmail(): string | undefined;
  now(): Date;
}

const defaultDependencies: AIUsageLimitsServiceDependencies = {
  async readFreeTierDocument() {
    const snapshot = await getDoc(doc(db, AI_CONFIG_COLLECTION, AI_USAGE_LIMITS_DOCUMENT_ID));
    return snapshot.exists() ? snapshot.data() : undefined;
  },
  async writeFreeTierDocument(payload) {
    await setDoc(doc(db, AI_CONFIG_COLLECTION, AI_USAGE_LIMITS_DOCUMENT_ID), payload, { merge: true });
  },
  async writePaidLeagueLimit(leagueID, payload) {
    await setDoc(doc(db, "leagueBilling", leagueID), payload, { merge: true });
  },
  requestSummary(body) {
    return apiRequest<unknown>("/ai-usage/summary", { method: "POST", body });
  },
  recordAudit: recordActionAudit,
  currentEmail: () => auth.currentUser?.email ?? undefined,
  now: () => new Date(),
};

function validateLimit(limit: AIUsageLimit): AIUsageLimit {
  try {
    return aiUsageLimitFirestoreData(limit) as unknown as AIUsageLimit;
  } catch {
    throw new AIUsageLimitInputError(
      "invalidTotalTokens",
      "Enter a whole-number token limit from 1 through 100,000,000,000.",
    );
  }
}

export async function loadFreeTierAIUsageLimit(
  dependencies: Pick<AIUsageLimitsServiceDependencies, "readFreeTierDocument"> = defaultDependencies,
): Promise<AIUsageLimit | null> {
  return parseFreeTierAIUsageLimit(await dependencies.readFreeTierDocument());
}

export async function saveFreeTierAIUsageLimit(
  limit: AIUsageLimit,
  currentUser: SBotUser | null,
  isSBotLabsAdmin: boolean,
  dependencies: Pick<
    AIUsageLimitsServiceDependencies,
    "writeFreeTierDocument" | "currentEmail" | "now"
  > = defaultDependencies,
): Promise<AIUsageLimit> {
  if (!isSBotLabsAdmin || !currentUser) {
    throw new AIUsageLimitInputError("notAuthorized", "SBot Labs admin access is required to manage AI usage limits.");
  }
  const normalized = validateLimit(limit);
  await dependencies.writeFreeTierDocument({
    freeTier: aiUsageLimitFirestoreData(normalized),
    updatedAt: dependencies.now(),
    updatedByEmail: dependencies.currentEmail() ?? currentUser.email,
    updatedByUserID: currentUser.id,
  });
  return normalized;
}

export async function savePaidLeagueAIUsageLimit(
  limit: AIUsageLimit | null,
  original: LeagueBilling,
  currentUser: SBotUser,
  isSBotLabsAdmin: boolean,
  dependencies: Pick<
    AIUsageLimitsServiceDependencies,
    "writePaidLeagueLimit" | "recordAudit" | "now"
  > = defaultDependencies,
): Promise<LeagueBilling> {
  if (!isSBotLabsAdmin) {
    throw new AIUsageLimitInputError("notAuthorized", "SBot Labs admin access is required to manage AI usage limits.");
  }
  if (original.tier !== "paid") {
    throw new AIUsageLimitInputError("notPaidTier", "Negotiated AI usage limits can only be saved for Paid Tier leagues.");
  }
  const normalizedLimit = limit ? validateLimit(limit) : null;
  const updatedAt = dependencies.now();
  const updated: LeagueBilling = {
    ...original,
    aiUsage: normalizedLimit,
    updatedAt,
    updatedByUserID: currentUser.id,
  };
  await dependencies.writePaidLeagueLimit(
    original.id,
    leagueAIUsageClientWritePayload(normalizedLimit, updatedAt, currentUser.id),
  );
  try {
    dependencies.recordAudit({
      before: leagueBillingFirestoreData(original),
      after: leagueBillingFirestoreData(updated),
      context: {
        leagueID: original.leagueID,
        targetCollection: "leagueBilling",
        targetDocumentID: original.id,
        targetDisplayName: original.leagueName,
        actionType: "update",
        actor: humanActionAuditActor(currentUser),
        fieldLabels: {
          "aiUsage.enabled": "AI Token Limit Enabled",
          "aiUsage.totalTokens": "AI Token Limit",
          "aiUsage.period": "AI Token Limit Period",
        },
      },
    });
  } catch {
    // Auditing is non-blocking: the successful negotiated-limit write remains successful.
  }
  return updated;
}

export async function fetchAIUsageSummary(
  request: AIUsageSummaryRequest = {},
  dependencies: Pick<AIUsageLimitsServiceDependencies, "requestSummary"> = defaultDependencies,
): Promise<AIUsageSummary> {
  const leagueID = request.leagueID?.trim();
  const body: Record<string, unknown> = {
    includeBreakdown: request.includeBreakdown === true,
    ...(leagueID ? { leagueID } : {}),
    ...(request.period ? { period: request.period } : {}),
  };
  return decodeAIUsageSummary(await dependencies.requestSummary(body));
}
